/**
 * Hooks de dados da auditoria via React Query
 */
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '@/lib/api';
import { useAuditFilters } from './useAuditFilters';
import type {
  AuditSummary,
  AuditDivergencesResponse,
  TaxUnit,
  StatusMismatchResponse,
  DeepAuditCoverage,
} from '@claro/shared';

const API_BASE = `${API_BASE_URL}/claro/api`;

async function fetchJson<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
  }

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  const json = await response.json();
  return json.data ?? json;
}

/**
 * Fetch que preserva a resposta paginada { data, total, page, limit, totalPages }
 * (não extrai json.data como o fetchJson faz)
 */
async function fetchPaginated<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
  }

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  const json = await response.json();
  const { status: _status, ...rest } = json;
  return rest as T;
}

/**
 * Fetch que extrai o campo `result` da resposta (usado pelos endpoints de spreadsheets/nxt-cross)
 */
async function fetchResult<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
  }

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  const json = await response.json();
  return json.result as T;
}

// ── Summary ─────────────────────────────────────────────────────────────────

export function useAuditSummary() {
  return useQuery({
    queryKey: ['audit', 'summary'],
    queryFn: () => fetchJson<AuditSummary>('/audit/summary'),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

// ── Divergences (paginated + filtered) ──────────────────────────────────────

export function useAuditDivergences() {
  const getFilterParams = useAuditFilters((s) => s.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['audit', 'divergences', params],
    queryFn: () => fetchPaginated<AuditDivergencesResponse>('/audit/divergences', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

// ── Not Found ───────────────────────────────────────────────────────────────

export function useAuditNotFound() {
  return useQuery({
    queryKey: ['audit', 'not-found'],
    queryFn: () => fetchJson<TaxUnit[]>('/audit/not-found'),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

// ── Status Mismatches (ativa PD × baixada tributário) ───────────────────────

export function useAuditStatusMismatches() {
  return useQuery({
    queryKey: ['audit', 'status-mismatches'],
    queryFn: () => fetchPaginated<StatusMismatchResponse>('/audit/status-mismatches'),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

export function useDeepAuditCoverage() {
  return useQuery({
    queryKey: ['audit', 'deep-coverage'],
    queryFn: () => fetchResult<DeepAuditCoverage>('/audit/deep/coverage', { _: String(Date.now()) }),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    refetchInterval: (query) => query.state.data?.running ? 5000 : false,
  });
}

export interface DeepAuditQueue {
  coverage: DeepAuditCoverage;
  stage: string;
  nextUnits: Array<{ identification: string; name: string; cnpj?: string; regional?: string }>;
}

export function useDeepAuditQueue() {
  return useQuery({
    queryKey: ['audit', 'deep-queue'],
    queryFn: () => fetchResult<DeepAuditQueue>('/audit/deep/queue', { _: String(Date.now()) }),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    refetchInterval: (query) => query.state.data?.coverage.running ? 5000 : false,
  });
}

// ── Claro NXT (fonte de ausentes no PropertyDocs) ───────────────────────────

export interface TaxSpreadsheetRecord {
  id: string;
  sourceType?: 'consolidated' | 'nxt-missing';
  originalName: string;
  uploadedAt: string;
  summary: {
    totalRows: number;
    activeRows: number;
    baixadaRows: number;
    ignoredRows: number;
    uniqueCnpjs: number;
    invalidCnpjs: number;
    reportedMissingRows?: number;
    sheetName: string;
  };
  reportedMissingCount?: number;
}

export interface NxtCrossSample {
  ordem?: string;
  cnpj: string;
  classification: 'found' | 'missing' | 'divergent' | 'ambiguous';
  identification: string;
  pdUnits: Array<{
    identification: string;
    name: string;
    regional: string;
    matchedBy: Array<'registered-number' | 'alvara' | 'address'>;
  }>;
  divergentFields?: string[];
}

export interface NxtCrossResult {
  propertyDocsExport?: { phase: string; error?: string; latest?: { completedAt: string } };
  registration?: NxtRegistration & { sourceName: string; uploadedAt: string; permitOnly: NxtRegistration };
  spreadsheet: TaxSpreadsheetRecord;
  generatedAt: string;
  summary: { total: number; found: number; missing: number; divergent: number; ambiguous: number };
  samples: NxtCrossSample[];
}

export interface NxtRegistration {
  total: number; registered: number; missing: number;
  byStatus: Array<{ status: string; registered: number; missing: number; total: number }>;
  rows: Array<{ cnpj: string; ordem: string; status: string; registered: boolean; units: string[]; warning: string; matchedBy?: string; taxAddress?: string; referenceOrder?: string }>;
}

/**
 * Retorna a fonte NXT ausente mais recente carregada (sourceType = 'nxt-missing')
 */
export function useLatestNxtSpreadsheet() {
  return useQuery({
    queryKey: ['audit', 'spreadsheets'],
    queryFn: async () => {
      const result = await fetchResult<{ activeId: string | null; files: TaxSpreadsheetRecord[] }>('/audit/spreadsheets');
      const nxtFiles = result.files
        .filter((file) => file.sourceType === 'nxt-missing')
        .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
      return nxtFiles[0] ?? null;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Cruza a fonte NXT ausente com as unidades PropertyDocs por CNPJ normalizado
 */
export function useNxtCross(spreadsheetId: string | undefined) {
  return useQuery({
    queryKey: ['audit', 'nxt-cross', spreadsheetId],
    refetchInterval: 60000,
    queryFn: () => fetchResult<NxtCrossResult>('/audit/nxt-cross', { spreadsheetId: spreadsheetId! }),
    enabled: Boolean(spreadsheetId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}
